import {
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import Button from "components/Button";
import { useThemeColors } from "theme";
import React, { Suspense } from "react";
import { useTheme } from "@mui/material";
import { buildTestId } from "helpers/testId";

/**
 * DOM contract: the dialog paper is `end-other-session-dialog`, with `-title`,
 * `-message`, `-confirm` and `-cancel`. It is shown when a second login is
 * detected, so a spec for concurrent sessions asserts on the paper.
 */
const TEST_ID = "end-other-session-dialog";

const ConfirmationDialogIcon = React.lazy(
  () => import("components/icons/ConfirmationDialogIcon"),
);

interface EndOtherSessionDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm?: () => void;
  title: string;
  message: string;
  confirmButtonText: string;
  cancelButtonText: string;
}

export default function EndOtherSessionDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmButtonText,
  cancelButtonText,
}: EndOtherSessionDialogProps) {
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";
  const colors = useThemeColors();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      slotProps={{
        paper: {
          "data-testid": TEST_ID,
          sx: {
            width: "680px",
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            borderRadius: "10px",
            overflow: "visible",
            position: "relative",
            background: colors.background.secondary,
            color: colors.text.primary,
            px: 5,
            pt: 12,
            pb: 4,
          },
        } as any
      }}
    >
      <Stack
        sx={{
          position: "absolute",
          top: "-78px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "158px",
          height: "158px",
          borderRadius: "50%",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background.secondary,
          boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.10)",
        }}
      >
        <Suspense fallback={null}>
          <ConfirmationDialogIcon iconType="WARNING" isDark={isDarkTheme} />
        </Suspense>
      </Stack>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={3} sx={{
          alignItems: "center"
        }}>
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              lineHeight: 1.2,
              color: colors.text.primary,
              textAlign: "center",
            }}
            data-testid={buildTestId(TEST_ID, "title")}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 400,
              lineHeight: 1.5,
              color: colors.text.primary,
              textAlign: "center",
              maxWidth: "590px",
            }}
            data-testid={buildTestId(TEST_ID, "message")}
          >
            {message}
          </Typography>

          <Stack
            direction="row"
            spacing={4}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              mt: 1
            }}>
            <Button
              onClick={onConfirm}
              variant="dangerFlat"
              data-testid={buildTestId(TEST_ID, "confirm")}
              sx={{
                width: "200px",
                height: "58px",
                borderRadius: "8px",
                fontSize: "20px",
                fontWeight: 500,
              }}
            >
              {confirmButtonText}
            </Button>

            <Button
              onClick={onCancel}
              variant="outlinedTertiary"
              data-testid={buildTestId(TEST_ID, "cancel")}
              sx={{
                width: "140px",
                height: "58px",
                borderRadius: "8px",
                fontSize: "20px",
                fontWeight: 400,

                "&:hover": {
                  borderColor: colors.text.tertiary,
                  backgroundColor: "transparent",
                },
              }}
            >
              {cancelButtonText}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
